<script lang="ts">
  import { Tabs, TabItem } from "flowbite-svelte";
  import Chatbot2 from "../chat/Chatbot2.svelte";
  import type { Project, Document } from "../../../services/api/types.js";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faExternalLink, faXmark, faChevronRight, faComments, faFolder, faDownload, faTimes } from "@fortawesome/free-solid-svg-icons";
  import { selectedObjects } from "../../../services/stores/selected-objects-store.js";
  import { getProjectsByCustomer, saveUserMetadata, getCustomer, getDocumentsByCustomer, getSASUrl } from "../../../services/api/data-handler.js";
  import JSZip from "jszip";
  import { getEnvironment } from "../../../config/env.js";
  import { onMount, onDestroy } from "svelte";
  import { Splitpanes, Pane } from "svelte-splitpanes";
  import { userStore } from "../../../services/stores/user-store.js";
  import { get } from "svelte/store";
  import { createEventDispatcher } from "svelte";
  import { logError, logInfo, logWarning } from "../../../services/logging/log-handler.js";
  
  // Subscribe to userStore for reactivity
  $: $userStore;

  export let customer_id: string;
  export let sessionToLoad: string | null = null;

  const dispatch = createEventDispatcher();

  let customerCode: string = "";
  let projects: Project[] = [];

  // Splitter position
  const MIN_WIDTH_REM = 12;
  
  // Initialize with default calculated value immediately
  function calculateDefaultPosition(): number {
    const viewportWidth = window.innerWidth;
    const remInPixels = 16; // Base font size
    const minWidthPixels = MIN_WIDTH_REM * remInPixels;
    return (minWidthPixels / viewportWidth) * 100;
  }

  let splitPosition: number = calculateDefaultPosition();
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let hasLoadedPosition = false;
  let lastSavedPosition: number | null = null; // Track last saved to avoid duplicate saves
  let pendingSave = false; // Track if there's a pending save

  // Load splitter position from user metadata when user store is ready (shared with DocumentsContent)
  $: {
    if ($userStore.user && !hasLoadedPosition) {
      if ($userStore.user.metadata?.content_context_splitter) {
        const saved = $userStore.user.metadata.content_context_splitter;
        if (typeof saved === "number" && saved > 0 && saved < 100) {
          splitPosition = saved;
          lastSavedPosition = saved;
          hasLoadedPosition = true;
        } else {
          hasLoadedPosition = true;
        }
      } else {
        hasLoadedPosition = true;
      }
    }
  }

  // Actually perform the save
  async function doSave() {
    // Skip if position hasn't changed from last save
    if (lastSavedPosition !== null && Math.abs(splitPosition - lastSavedPosition) < 0.5) {
      pendingSave = false;
      return;
    }
    
    try {
      const store = get(userStore);
      if (!store.user) return;
      const newMetadata = {
        ...store.user.metadata,
        content_context_splitter: splitPosition,
      };
      await saveUserMetadata(newMetadata);
      lastSavedPosition = splitPosition;
    } catch (error) {
      logError(error, "ProjectsContent", undefined, { message: "Error saving splitter position" });
    } finally {
      pendingSave = false;
    }
  }

  // Save splitter position to user metadata (debounced, shared with DocumentsContent)
  function saveSplitterPosition() {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    pendingSave = true;
    saveTimeout = setTimeout(() => {
      doSave();
      saveTimeout = null;
    }, 1000); // Debounce by 1000ms (increased from 500ms)
  }

  // Handle splitter resize
  function handleSplitterResized(event: CustomEvent) {
    const panes = event.detail as Array<{ size: number; min?: number; max?: number }>;
    if (panes && Array.isArray(panes) && panes.length > 0 && panes[0]?.size !== undefined) {
      splitPosition = panes[0].size;
      saveSplitterPosition();
    }
  }

  // Stage toggle management
  const STAGE_STORAGE_KEY = "chatStage";
  const VALID_STAGES = [
    "protocol",
    "screening",
    "data_extraction",
    "report",
    "included_publications",
  ];
  let stage: string = "report";
  let chatToggleStages = [
    {
      key: "protocol",
      label: "Protocol",
      description: "Filter chat to this protocol stage",
    },
    {
      key: "screening",
      label: "Screening",
      description: "Filter chat to this screening stage",
    },
    {
      key: "included_publications",
      label: "Included publications",
      description: "Filter chat to this included publications stage",
    },
    {
      key: "data_extraction",
      label: "Data extraction",
      description: "Filter chat to this data extraction stage",
    },
    {
      key: "report",
      label: "Report",
      description: "Filter chat to this reporting stage",
    },
  ];

  // Cache all projects for current customer to avoid refetching on selection changes
  let allProjectsForCustomer: Project[] = [];
  let lastLoadedCustomerForProjects: string | null = null;

  // Load customer code if not available from projects
  async function ensureCustomerCode() {
    if (!customerCode && customer_id) {
      try {
        const customerData = await getCustomer(customer_id);
        if (customerData?.customer) {
          customerCode = customerData.customer.code;
        }
      } catch (error) {
        logError(error, "ProjectsContent", undefined, { message: "Error loading customer" });
      }
    }
  }

  function loadData() {
    if ($selectedObjects.projects.length > 0) {
      // Map selected projects using cached list
      projects = $selectedObjects.projects
        .map((selectedProject) =>
          allProjectsForCustomer.find(
            (p) => p.project_id === selectedProject.project_id,
          ),
        )
        .filter((project): project is Project => project !== undefined);

      // Set the first project as selected by default
      if (projects.length > 0) {
        customerCode = projects[0].customer.code;
      }
    } else {
      projects = [];
      // Ensure customer code is loaded even without projects
      ensureCustomerCode();
    }
  }

  // Load cache once per customer_id change
  $: {
    if (customer_id && customer_id !== lastLoadedCustomerForProjects) {
      getProjectsByCustomer(customer_id)
        .then((projectsData) => {
          allProjectsForCustomer = projectsData?.projects || [];
          lastLoadedCustomerForProjects = customer_id;
          loadData();
        })
        .catch(() => {
          allProjectsForCustomer = [];
          lastLoadedCustomerForProjects = customer_id;
          loadData();
        });
    }
  }

  // Recompute visible selection when selected projects change (without refetching)
  $: {
    if ($selectedObjects.projects) {
      loadData();
    }
  }

  function safeGet(data: any, path: string, defaultValue: any = "N/A") {
    try {
      return (
        path
          .split(".")
          .reduce((acc: any, part: string) => acc?.[part], data as any) ??
        defaultValue
      );
    } catch {
      return defaultValue;
    }
  }

  // Helper functions to safely access document properties without type assertions
  function getDocFilename(doc: any): string {
    return doc.doc_filename || doc.file_name || "";
  }

  function getDocPathFromDoc(doc: any): string {
    return doc.doc_path || doc.file_name || doc.doc_filename || "";
  }

  // Initialize stage from localStorage
  function initializeStage() {
    try {
      const raw = localStorage.getItem(STAGE_STORAGE_KEY);
      if (raw === null) {
        stage = "report"; // Default to "Report"
        localStorage.setItem(STAGE_STORAGE_KEY, stage);
      } else {
        const normalized = raw.trim();
        stage = VALID_STAGES.includes(normalized) ? normalized : "report";
        localStorage.setItem(STAGE_STORAGE_KEY, stage);
      }
    } catch (e) {
      stage = "report"; // Default to "Report" on error
    }
  }

  function selectChatStageToggle(key: string) {
    // Set the selected stage (empty string for "All")
    stage = key;
    try {
      localStorage.setItem(STAGE_STORAGE_KEY, stage);
    } catch {}
  }

  // Download all documents from selected projects as ZIP
  async function downloadAllAsZip() {
    if (!customer_id || projects.length === 0) return;

    async function fetchWithRetry(
      url: string,
      maxAttempts = 3,
      timeout = 60000
    ): Promise<Blob> {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);

          const response = await fetch(url, {
            signal: controller.signal,
            credentials: "include",
            method: "GET",
            headers: {
              Accept: "*/*",
            },
            mode: "cors",
          });

          clearTimeout(timeoutId);

          if (!response.ok)
            throw new Error(`HTTP error! status: ${response.status}`);
          return await response.blob();
        } catch (error) {
          logWarn("Attempt " + attempt + " failed", "ProjectsContent", undefined, { error: error });
          if (attempt === maxAttempts) throw error;
          await new Promise((resolve) => setTimeout(resolve, attempt * 2000));
        }
      }
      throw new Error("Failed to fetch after all attempts");
    }

    try {
      // Get all documents for the customer
      const documentsData = await getDocumentsByCustomer(customer_id);
      const allDocuments = documentsData?.documents || [];

      // Filter documents to only those from selected projects
      const projectIds = new Set(projects.map((p) => p.project_id));
      const documentsToDownload = allDocuments.filter((doc: Document) =>
        projectIds.has(doc.project_id)
      );

      if (documentsToDownload.length === 0) {
        return;
      }

      const files = documentsToDownload.map(async (doc: Document) => {
        // For SAS URL, we need doc_path (storage name), not doc_filename (display name)
        const storageFileName = getDocPathFromDoc(doc);
        const url = await getSASUrl(
          doc.customer_code,
          doc.project_id,
          storageFileName
        );
        // For download filename, use doc_filename (display name) or file_name, then doc_title
        const displayFileName = getDocFilename(doc) || doc.doc_title || storageFileName;
        return {
          url,
          filename: doc.doc_title || displayFileName,
        };
      });

      const resolvedFiles = await Promise.all(files);
      const zip = new JSZip();

      try {
        const customer_code = customerCode || documentsToDownload[0]?.customer_code || "documents";
        const filePromises = resolvedFiles.map(async ({ url, filename }) => {
          const blob = await fetchWithRetry(url);
          zip.file(filename, blob);
        });

        await Promise.all(filePromises);

        const zipBlob = await zip.generateAsync({ type: "blob" });
        const zipUrl = window.URL.createObjectURL(zipBlob);
        const link = document.createElement("a");
        link.href = zipUrl;
        link.download = `${customer_code} selected-projects-documents.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(zipUrl);
      } catch (error) {
        logError(error, "ProjectsContent", undefined, { message: "Error creating zip" });
      }
    } catch (error) {
      logError(error, "ProjectsContent", undefined, { message: "Error downloading documents" });
    }
  }

  // Clear all selected projects
  function clearAll() {
    selectedObjects.clear();
  }

  // Handle project removal
  function handleRemoveProject(project: Project, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (!project.project_id || typeof project.project_id !== "string" || project.project_id.trim() === "") {
      logError(new Error("Cannot remove project: missing required field 'project_id'"), "ProjectsContent", undefined);
      return;
    }
    selectedObjects.removeProject(project.project_id.trim());
  }

  // Initialize stage on mount and ensure customer code
  onMount(() => {
    initializeStage();
    ensureCustomerCode();
  });

  onDestroy(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    // Save immediately on destroy if there's a pending save
    if (pendingSave) {
      doSave();
    }
  });
</script>

<div class="project-content-layout">
  <Splitpanes class="my-theme" on:resized={handleSplitterResized}>
    <!-- Left Panel - Context Panel -->
    <Pane size={splitPosition} minSize={((MIN_WIDTH_REM * 16) / window.innerWidth) * 100}>
      <div class="projects-panel">
        <div class="projects-header">
          <div class="sub-tab">CONTEXT</div>
          {#if projects.length > 0}
            <div class="documents-header-actions">
              <button type="button" class="action-button" on:click={downloadAllAsZip} title="Download All" >
                <FontAwesomeIcon icon={faDownload} />
              </button>
              <button type="button" class="action-button" on:click={clearAll} title="Clear All">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          {/if}
        </div>
        <div class="projects-list">
          {#if projects.length > 0}
            <div class="projects-list-label">
              {projects.length > 1 ? "Project(s)" : "Project(s)"}
            </div>
            {#each projects as project}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="project-item">
                <div class="project-item-content">
                  <FontAwesomeIcon icon={faFolder} class="project-icon mr-1" />
                  <p class="project-name text-value">{safeGet(project, "name")}</p>
                  <button type="button" class="action-button" on:click={(e) => handleRemoveProject(project, e)} title="Remove project">
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div class="therapeutic-area-badge">
                  {safeGet(project, "metadata.therapeutic_area.name")}
                </div>
                <hr class="line my-1" />
              </div>
            {/each}
          {:else}
            <div class="no-projects">
              <div class="placeholder animate-pulse"></div>
              <div class="placeholder animate-pulse"></div>
              <div class="placeholder animate-pulse"></div>
            </div>
          {/if}
        </div>
        {#if projects && projects.length > 0}
          <div class="projects-stage-toggles">
            <div class="projects-stage-label">Stage</div>
            <div class="stage-options">
              {#each chatToggleStages as chatStage}
                <label class="stage-option">
                  <input
                    type="radio"
                    name="stage"
                    value={chatStage.key}
                    checked={stage === chatStage.key}
                    on:change={() => selectChatStageToggle(chatStage.key)}
                    class="stage-radio"
                  />
                  <span class="stage-option-label" class:selected={stage === chatStage.key}>{chatStage.label}</span>
                </label>
              {/each}
            </div>
          </div>
        {/if}
      </div>
    </Pane>

    <!-- Right Panel - Content Panel -->
    <Pane>
      <div class="chat-panel">
        <Tabs tabStyle="underline" contentClass=" rounded-lg" class="flex-nowrap overflow-x-auto tabs-container">
          <TabItem class="results-explorer-tab min-w-max" open={true}>
            <div slot="title" class="sub-tab">CHAT
              <!-- svelte-ignore a11y-invalid-attribute -->
              <a
                href="javascript:void(0)"
                on:click={() => {
                  const url = `${getEnvironment().webappUrl}/chatbot?customer=${customerCode}&projects=${encodeURIComponent(JSON.stringify(projects.map((p) => p.project_id)))}`;
                  window.open(
                    url,
                    "_blank",
                    "width=1200,height=800,left=200,top=200",
                  );
                }}
                class="external-link"><FontAwesomeIcon icon={faExternalLink} /></a
              >
            </div>
            <Chatbot2 
              customer_code={customerCode}
              {projects} {stage} 
              sessionToLoad={sessionToLoad} 
              customer_id={customer_id} 
              on:sessionLoaded={() => dispatch("sessionLoaded")}
              on:stageChange={(e) => {
                const newStage = e.detail?.stage;
                if (newStage && VALID_STAGES.includes(newStage)) {
                  selectChatStageToggle(newStage);
                }
              }} />
          </TabItem>
        </Tabs>
      </div>
    </Pane>
  </Splitpanes>
</div>
